import { ActionLink, Caption, useDialog } from '@if/mui'
import { Stack } from '@mui/material'
import { useMerchantResources } from '@src/data/merchant-config-selector'
import { MerchantResource } from '@src/types/MerchantResource'
import { Merchant } from '@src/types/Merchant'
import { useTranslation } from 'react-i18next'

const openResource = (resource: MerchantResource) => {
  const link = document.createElement('a')
  link.href = resource.href
  link.setAttribute('download', resource.name)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const ResourceRow = ({ resource }: { resource: MerchantResource }) =>
  resource.download ? (
    <ActionLink onClick={() => openResource(resource)}>{resource.name}</ActionLink>
  ) : (
    <ActionLink href={resource.href} target="_blank">
      {resource.name}
    </ActionLink>
  )

export const useResourcesDialog = (merchant: Merchant) => {
  const { t } = useTranslation()
  const resources = useMerchantResources(merchant)

  const dialog = useDialog<MerchantResource[], void>({
    title: t('nav.resources'),
    maxWidth: 'xs',
    fullWidth: true,
    edgeToEdge: true,
    hideConfirm: true,
    cancelLabel: t('common.close'),
    content: (ctx) => {
      const programs = [...new Set(ctx.props.map((resource) => resource.program))]
      return (
        <Stack sx={{ p: 2 }} spacing={2}>
          {programs.map((program) => (
            <Stack key={program ?? 'libraries'}>
              {program !== undefined && programs.length > 1 && (
                <Caption>{t(`EFinancingProgram.${program}`, { ns: 'enum' })}</Caption>
              )}
              {ctx.props
                .filter((resource) => resource.program === program)
                .map((resource) => (
                  <ResourceRow key={resource.href} resource={resource} />
                ))}
            </Stack>
          ))}
        </Stack>
      )
    },
  })

  return { resources, open: () => dialog.open(resources) }
}
